import React, { useState } from 'react';

const FollowUpEmailStep = ({ email, setEmail, onLookup, isLoading, error }) => {
  const [inputEmail, setInputEmail] = useState(email || '');
  const [inputPhone, setInputPhone] = useState('');
  const [lookupMode, setLookupMode] = useState('email'); // 'email' or 'phone'
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (lookupMode === 'email' && inputEmail.trim()) {
      setEmail(inputEmail.trim());
      onLookup(inputEmail.trim(), null);
    } else if (lookupMode === 'phone' && inputPhone.trim()) {
      // Clean phone number
      const cleanPhone = inputPhone.replace(/\D/g, '');
      onLookup(null, cleanPhone);
    }
  };
  
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const phone = value.replace(/\D/g, '');
    // Format as (XXX) XXX-XXXX
    if (phone.length <= 3) return phone;
    if (phone.length <= 6) return `(${phone.slice(0, 3)}) ${phone.slice(3)}`;
    return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6, 10)}`;
  };
  
  return (
    <div data-testid="followup-email-step">
      {/* Welcome Message */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-[#e8f5ee] rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-2">
          Weight Loss Follow-Up
        </h2>
        <p className="text-gray-500">
          Enter your email or phone number to access your personalized follow-up questionnaire
        </p>
      </div>
      
      {/* Toggle between email and phone */}
      <div className="flex justify-center gap-2 mb-6">
        <button
          type="button"
          onClick={() => setLookupMode('email')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            lookupMode === 'email' 
              ? 'bg-[#2d8a5f] text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Email
        </button>
        <button
          type="button"
          onClick={() => setLookupMode('phone')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            lookupMode === 'phone' 
              ? 'bg-[#2d8a5f] text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Phone Number
        </button>
      </div>
      
      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {lookupMode === 'email' ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={inputEmail}
              onChange={(e) => setInputEmail(e.target.value)}
              placeholder="Enter your registered email"
              className="form-input"
              data-testid="email-input"
              required
              disabled={isLoading}
            />
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={inputPhone}
              onChange={(e) => setInputPhone(formatPhoneNumber(e.target.value))}
              placeholder="(555) 123-4567"
              className="form-input"
              data-testid="phone-input"
              required
              disabled={isLoading}
            />
            <p className="text-xs text-gray-400 mt-1">Enter your 10-digit phone number</p>
          </div>
        )}
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-red-700" data-testid="error-message">{error}</p>
            </div>
          </div>
        )}
        
        <button
          type="submit"
          className="btn-primary"
          data-testid="lookup-button"
          disabled={isLoading || (lookupMode === 'email' ? !inputEmail.trim() : inputPhone.replace(/\D/g, '').length !== 10)}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Looking up...
            </span>
          ) : (
            'Continue'
          )}
        </button>
      </form>
      
      {/* Info */}
      <div className="mt-8 text-center">
        <p className="text-xs text-gray-400">
          Your information will be pre-filled based on your previous records.
        </p>
      </div>
    </div>
  );
};

export default FollowUpEmailStep;
